// src/features/promoteur/ChargilyPaymentSuccess.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "./useSubscription";

const MAX_ATTEMPTS  = 10;
const POLL_INTERVAL = 3000;

export default function ChargilyPaymentSuccess() {
  const { user, role, isLoading: authLoading } = useAuth();
  const { isSubscribed, expiryDate, daysLeft, loading: subLoading, refetch } = useSubscription(user?.uid);

  const [attempts, setAttempts] = useState(0);
  const [polling, setPolling]   = useState(true);

  const loading = authLoading || subLoading;

  // ── Polling jusqu'à activation ───────────────────────────────────
  useEffect(() => {
    if (isSubscribed || attempts >= MAX_ATTEMPTS || !user?.uid) {
      setPolling(false);
      return;
    }
    if (loading) return;

    const timer = setTimeout(() => {
      setAttempts(prev => prev + 1);
      refetch?.();
    }, POLL_INTERVAL);

    return () => clearTimeout(timer);
  }, [isSubscribed, attempts, user?.uid, loading, refetch]);

  const formattedExpiry = expiryDate?.toLocaleDateString("fr-DZ", {
    day: "numeric", month: "long", year: "numeric",
  });

  // 🆕 Lien espace selon le rôle
  const espaceLink  = role === "promoteur" ? "/promoteur/dashboard" : "/client/dashboard";
  const espaceLabel = role === "promoteur" ? "Mon espace promoteur" : "Mon espace client";

  // ── Statut affiché ───────────────────────────────────────────────
  const renderStatus = () => {
    if (loading) {
      return (
        <StatusBox color="#f9fafb" border="#e5e7eb" textColor="#6b7280">
          <Spinner />
          Vérification de votre abonnement...
        </StatusBox>
      );
    }

    if (isSubscribed) {
      return (
        <div style={{ padding: "14px 16px", background: "#f0faf6", borderRadius: "12px", border: "1px solid #a3d9c0", marginBottom: "24px" }}>
          <p style={{ margin: "0 0 4px", fontSize: "15px", fontWeight: "600", color: "#0f6e56" }}>
            ✅ Abonnement actif — {daysLeft} jours restants
          </p>
          {formattedExpiry && (
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              Expire le {formattedExpiry}
            </p>
          )}
        </div>
      );
    }

    if (polling && attempts < MAX_ATTEMPTS) {
      return (
        <div style={{ padding: "14px 16px", background: "#fffbeb", borderRadius: "12px", border: "1px solid #fcd34d", marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "center", marginBottom: "6px" }}>
            <Spinner color="#92400e" />
            <p style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#92400e" }}>
              Activation en cours...
            </p>
          </div>
          <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
            Tentative {attempts + 1}/{MAX_ATTEMPTS} — mise à jour automatique dans quelques secondes
          </p>
        </div>
      );
    }

    return (
      <div style={{ padding: "14px 16px", background: "#fef2f2", borderRadius: "12px", border: "1px solid #fca5a5", marginBottom: "24px" }}>
        <p style={{ margin: "0 0 6px", fontSize: "14px", fontWeight: "600", color: "#dc2626" }}>
          ⚠️ Activation en attente
        </p>
        <p style={{ margin: 0, fontSize: "13px", color: "#6b7280", lineHeight: "1.6" }}>
          Votre paiement a bien été reçu. L'activation peut prendre quelques minutes.
          Rafraîchissez cette page ou revenez dans quelques instants.
        </p>
        <button
          onClick={() => { setAttempts(0); setPolling(true); }}
          style={{ marginTop: "10px", background: "none", border: "1px solid #dc2626", color: "#dc2626", padding: "6px 14px", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}
        >
          🔄 Réessayer
        </button>
      </div>
    );
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f7f8fa", padding: "20px" }}>
      <div style={{ width: "100%", maxWidth: "520px", background: "#ffffff", borderRadius: "20px", padding: "40px 30px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", textAlign: "center" }}>

        {/* Icône */}
        <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "#e9f8f0", color: "#078D3F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "34px", margin: "0 auto 20px", fontWeight: "bold" }}>
          ✓
        </div>

        <h1 style={{ margin: "0 0 10px", fontSize: "26px", fontWeight: "700", color: "#111827" }}>
          Paiement réussi
        </h1>
        <p style={{ margin: "0 0 24px", fontSize: "15px", lineHeight: "1.6", color: "#6b7280" }}>
          Votre paiement a bien été effectué. Votre abonnement est en cours d'activation.
        </p>

        {/* Statut */}
        {renderStatus()}

        {/* Boutons — 🆕 lien selon le rôle */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
          {isSubscribed && (
            <Link
              to={espaceLink}
              style={{ textDecoration: "none", background: "#078D3F", color: "#fff", padding: "12px 22px", borderRadius: "10px", fontWeight: "600", fontSize: "14px" }}
            >
              {espaceLabel}
            </Link>
          )}
          <Link
            to="/"
            style={{ textDecoration: "none", background: "#f3f4f6", color: "#111827", padding: "12px 22px", borderRadius: "10px", fontWeight: "600", fontSize: "14px" }}
          >
            Retour à l'accueil
          </Link>
        </div>

      </div>
    </div>
  );
}

function Spinner({ color = "#6b7280" }) {
  return (
    <>
      <div style={{
        width: "16px", height: "16px", flexShrink: 0,
        border: "2px solid #e5e7eb",
        borderTop: `2px solid ${color}`,
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        display: "inline-block",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

function StatusBox({ color, border, textColor, children }) {
  return (
    <div style={{
      padding: "14px 16px", background: color, borderRadius: "12px",
      border: `1px solid ${border}`, marginBottom: "24px",
      fontSize: "14px", color: textColor,
      display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
    }}>
      {children}
    </div>
  );
}